/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import i18next from 'i18next'
import * as R from 'radash'

import {
  Array,
  AsyncStateN,
  pipe,
  Random,
  Record,
  Result,
} from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  reatomGenerateMnemonic,
  requestLocalPasswordAction,
} from '@root/features/secure-storage/atoms'
import { ensureStorageIsInitializedAction } from '@root/features/secure-storage/atoms/reatom-create-password'
import { WrongPasswordError } from '@root/features/secure-storage/secure-storage-error'
import { reatomCreateWallet } from '@root/features/wallet/create-wallet'
import { CancelError, UnknownError } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import {
  showErrorCodeSnackbarAction,
  showErrorSnackbarAction,
} from '@root/shared/ui/error-snackbar'
import { SecureStorageErrorCode } from '@root/shared/ui/secure-storage-error-code'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { Redacted } from '@blade/std-lib'
import type { Ctx } from '@reatom/framework'

export type NewWalletStep =
  | 'acknowledgements'
  | 'seed-phrase'
  | 'seed-phrase-confirm'

export type MnemonicQuiz = {
  items: Array<{
    index: number
    options: Array<string>
    answer: string
  }>
}

export const reatomCreateWalletFlow = ({
  onSeedPhraseGenerated,
  onFinish,
}: {
  onSeedPhraseGenerated?: (ctx: Ctx) => void
  onFinish: (ctx: Ctx) => void
}) => {
  const stepAtom = atom<NewWalletStep>('acknowledgements')

  const { deviceWalletStateAtom, generateWalletAction, finishCreationAction } =
    reatomCreateWallet()

  const mnemonicStateAtom = reatomGenerateMnemonic()

  const mnemonicsAtom = atom((ctx): Array<string> => {
    const state = ctx.spy(mnemonicStateAtom)
    if (!state.value) {
      return []
    }
    return state.value.split(' ')
  }, 'mnemonicsAtom')

  const mnemonicQuizAtom = atom((ctx): MnemonicQuiz => {
    const result: MnemonicQuiz = {
      items: [],
    }

    const mnemonics = ctx.spy(mnemonicsAtom)

    const indexes = [0, 3, 6, 8]

    for (const index of indexes) {
      const word = mnemonics.at(index)!
      result.items.push({
        answer: word,
        index: index,
        options: R.shuffle([
          word,
          ...Random.elements(Array.without(word)(mnemonics), 2),
        ]),
      })
    }

    return result
  }, 'mnemonicQuizAtom')

  const selectedWordsAtom = atom<Record<number, string>>(
    {},
    'selectedWordsAtom',
  )

  const selectWordAction = action((ctx, index: number, word: string) => {
    selectedWordsAtom(ctx, (it) => pipe(it, Record.set(String(index), word)))
  }, 'selectWordAction')

  const confirmationValidAtom = atom((ctx) => {
    const quiz = ctx.spy(mnemonicQuizAtom)
    const answers = ctx.spy(selectedWordsAtom)

    for (const it of quiz.items) {
      if (answers[it.index] !== it.answer) {
        return false
      }
    }

    return true
  }, 'confirmationValidAtom')

  const generateAction = action(async (ctx) => {
    const result = await ctx.schedule(() => mnemonicStateAtom.execute(ctx))
    if (Result.isSuccess(result)) {
      onSeedPhraseGenerated?.(ctx)
      stepAtom(ctx, 'seed-phrase')
    }
  }, 'generateAction')

  const confirmSeedPhraseAction = action(async (ctx) => {
    const mnemonic = ctx.get(mnemonicStateAtom).value
    if (!mnemonic) {
      return Result.fail(UnknownError.fromUnknown('invalid input'))
    }

    const processCreation = async ({
      password,
    }: {
      password: Redacted.Redacted
    }) => {
      const network = ctx.get(selectedNetworkAtom)
      const result = await ctx.schedule(() =>
        generateWalletAction(ctx, {
          network,
          password: password,
          mnemonic,
        }),
      )

      if (Result.isFailure(result)) {
        if (result.failure instanceof WrongPasswordError) {
          return result
        }
        showErrorSnackbarAction(
          ctx,
          UnknownError.fallbackToUnknown(result.failure),
        )
        return Result.fail(result.failure)
      }

      const createResult = await ctx.schedule(() => finishCreationAction(ctx))
      if (Result.isFailure(createResult)) {
        showErrorCodeSnackbarAction(
          ctx,
          createResult.failure instanceof UnknownError
            ? undefined
            : SecureStorageErrorCode.AlreadyExistsAddress,
        )
        return Result.fail(createResult.failure)
      }
      return createResult
    }

    const passwordResult = await ctx.schedule(() =>
      ensureStorageIsInitializedAction(ctx),
    )

    if (Result.isFailure(passwordResult)) {
      return Result.fail(passwordResult.failure)
    }

    const password = passwordResult.success.password
    if (password) {
      return processCreation({ password })
    }

    return requestLocalPasswordAction(ctx, async (ctx, { password }) => {
      const result = await ctx.schedule(() => processCreation({ password }))
      return Result.unify(result)
    })
  }, 'confirmSeedPhraseAction')

  const confirmStateAtom = reatomRequest({
    execAction: confirmSeedPhraseAction,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        const error = result.failure
        if (
          error instanceof CancelError ||
          error instanceof WrongPasswordError
        ) {
          confirmStateAtom.setState(ctx, AsyncStateN.notInitiated())
          return
        }

        showErrorCodeSnackbarAction(
          ctx,
          error instanceof UnknownError
            ? undefined
            : SecureStorageErrorCode.AlreadyExistsAddress,
        )
        return
      }

      showSnackbarAction(ctx, {
        text: i18next.t('wallet.create.success-message'),
        intent: 'success',
      })
      onFinish(ctx)
    },
  })

  return {
    mnemonicQuizAtom,
    deviceWalletStateAtom,
    generateAction,
    confirmStateAtom,
    selectWordAction,
    selectedWordAtom: selectedWordsAtom,
    confirmationValidAtom,
    mnemonic: mnemonicsAtom,
    step: stepAtom,
  }
}

export type CreateWalletFlowModel = ReturnType<typeof reatomCreateWalletFlow>
